import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// "Working on now" card for the top of the Resources page. When a block is
// running on the user's calendar right now and it's linked to a project, this
// surfaces that project's resources + ad-hoc links front and centre — the same
// data the Calendar active-block widget and Focus mode use, so opening
// Resources mid-task lands you on exactly what you need. Shows whenever a
// project-linked block is running (matching the Calendar widget) — with a
// small note when that project has no resources linked yet. Hidden only when
// nothing's running or the running block isn't tied to a project.
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import BlockResources from "./BlockResources";
const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
function dateStr(offsetDays) {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
const BLOCK_LABEL = {
    lockout: "Deep Focus",
    flex: "Flex",
    personal: "Personal",
};
function fmtClock(iso) {
    const d = new Date(iso);
    let h = d.getHours();
    const mm = d.getMinutes().toString().padStart(2, "0");
    const ap = h < 12 ? "am" : "pm";
    if (h === 0)
        h = 12;
    else if (h > 12)
        h -= 12;
    return `${h}:${mm} ${ap}`;
}
export default function NowResourcesCard() {
    const [blocks, setBlocks] = useState([]);
    const [now, setNow] = useState(Date.now());
    // null = BlockResources hasn't reported yet for the current project; we keep
    // the card hidden until we know there's at least one resource to show.
    const [count, setCount] = useState(null);
    const load = useCallback(async () => {
        try {
            const bs = await api.get(`/time-blocks?from=${dateStr(0)}&to=${dateStr(1)}&tz=${encodeURIComponent(BROWSER_TZ)}`);
            setBlocks(bs);
        }
        catch {
            /* non-fatal — the card just stays hidden */
        }
    }, []);
    useEffect(() => {
        void load();
    }, [load]);
    // Pick up newly created / edited blocks when the user returns to the tab.
    useEffect(() => {
        const onFocus = () => void load();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [load]);
    // Tick so the countdown stays live and block transitions are picked up from
    // the already-fetched list without re-hitting the API every second.
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);
    const active = blocks.find((b) => {
        const s = new Date(b.start_at).getTime();
        const e = new Date(b.end_at).getTime();
        return now >= s && now < e;
    }) ?? null;
    // Reset the known count whenever the active project changes — that's exactly
    // when BlockResources refetches and will report a fresh onLoaded — so we hide
    // until the new block's resources resolve instead of flashing stale chrome.
    const activeProjectId = active?.project_id ?? null;
    const prevPidRef = useRef(undefined);
    useEffect(() => {
        if (prevPidRef.current !== activeProjectId) {
            prevPidRef.current = activeProjectId;
            setCount(null);
        }
    }, [activeProjectId]);
    if (!active || !active.project_id)
        return null;
    const endMs = new Date(active.end_at).getTime();
    const remainingMin = Math.max(0, Math.ceil((endMs - now) / 60000));
    const kind = BLOCK_LABEL[active.block_type] ?? active.block_type;
    const sub = [
        kind,
        active.project_name,
        `${fmtClock(active.start_at)} – ${fmtClock(active.end_at)}`,
        remainingMin > 0 ? `${remainingMin} min left` : "time's up",
    ]
        .filter(Boolean)
        .join(" · ");
    return (_jsxs("div", { className: "now-resources-card", children: [_jsxs("div", { className: "now-resources-top", children: [_jsx("span", { className: `today-block-dot dot-${active.block_type} now-resources-dot`, "aria-hidden": "true" }), _jsxs("div", { className: "now-resources-meta", children: [_jsx("div", { className: "now-resources-eyebrow", children: "Working on now \u00B7 from your schedule" }), _jsx("div", { className: "now-resources-title", children: active.title }), _jsx("div", { className: "now-resources-sub", children: sub })] }), _jsx("span", { className: "now-resources-pill", children: "NOW" }), _jsx(Link, { to: "/focus", className: "now-resources-focus", children: "Focus \u2197" })] }), _jsx(BlockResources, { projectId: active.project_id, variant: "panel", onLoaded: setCount }), count === 0 && (_jsx("p", { className: "now-resources-empty muted small", children: "No resources linked to this block's project yet \u2014 add them on the project page and they'll show here." }))] }));
}
